import { useMemo } from 'react';

import {
  AddchartOutlined,
  DashboardOutlined,
  Launch,
  ListAltOutlined,
  PersonOutlined
} from '@mui/icons-material';
import { List } from '@mui/material';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import NavigationItem, { NavigationItemData } from '@/components/NavBar/components/NavigationItem';
import { useDashboardMenuItems } from '@/components/NavBar/hooks/useDashboardMenuItems';
import { useMarketsNavigationItems } from '@/components/NavBar/hooks/useMarketsNavigationItems';
import { METADATA_FIRST_NAME_FIELD, METADATA_LAST_NAME_FIELD } from '@/constants/constants';
import { PERSONALIZATION_FLAG } from '@/constants/featureFlags';
import { useAppSelector } from '@/hooks/redux-hooks';
import { retrieveFromUserMetadata } from '@/utils/retrieveFromUserMetadata';

const NavBar = () => {
  const { userEmail, metadata } = useAppSelector(state => state.auth);
  const activeDataHubId = useAppSelector(state => state.dataHub.activeDataHubId);
  const isPersonalizationEnabled = useFeatureFlagEnabled(PERSONALIZATION_FLAG);
  const { firstName, lastName } = useMemo(() => {
    return {
      firstName: retrieveFromUserMetadata(metadata || [], METADATA_FIRST_NAME_FIELD),
      lastName: retrieveFromUserMetadata(metadata || [], METADATA_LAST_NAME_FIELD)
    };
  }, [metadata]);
  const { menuItems: marketMenuItems } = useMarketsNavigationItems();
  const dashboardMenuItems = useDashboardMenuItems();

  const userName = useMemo(() => {
    if (!!firstName || !!lastName) {
      return (firstName ? `${firstName} ` : '') + lastName;
    }
    return userEmail;
  }, [firstName, lastName, userEmail]);

  const profileItems: NavigationItemData[] = [
    {
      key: 'settings',
      name: 'Settings',
      path: '/profile'
    },
    {
      key: 'logout',
      name: 'Logout',
      path: '/logout'
    }
  ];

  const dataHubBasePath = activeDataHubId ? `/data-hub/${activeDataHubId}/` : '/data-hub/';

  const dataHubItems: NavigationItemData[] = [
    {
      key: 'summary',
      name: 'Summary',
      path: `${dataHubBasePath}summary`
    },
    {
      key: 'preview',
      name: 'Preview',
      path: `${dataHubBasePath}preview`
    },
    {
      key: 'chart',
      name: 'Chart',
      path: `${dataHubBasePath}chart`
    },
    {
      key: 'export',
      name: 'Export',
      path: `${dataHubBasePath}export`
    }
  ];

  const catalogueItems: NavigationItemData[] = [
    {
      key: 'spot',
      name: 'Spot',
      path: '/catalog/spot'
    },
    {
      key: 'fwdCurve',
      name: 'Forward Curves',
      path: '/catalog/fwd'
    }
  ];

  const navigationBarItems: NavigationItemData[] = [
    {
      key: 'markets',
      name: 'Markets',
      icon: <DashboardOutlined />,
      path: '/markets',
      children: marketMenuItems
    },
    {
      key: 'catalog',
      name: 'Catalog',
      icon: <ListAltOutlined />,
      path: '/catalog',
      children: catalogueItems
    },
    {
      key: 'data-hub',
      name: 'Data Hub',
      icon: <Launch />,
      path: '/data-hub',
      children: dataHubItems
    },
    {
      key: 'profile',
      name: userName ?? '',
      icon: <PersonOutlined />,
      path: '/profile',
      children: profileItems
    }
  ];
  if (isPersonalizationEnabled) {
    navigationBarItems.unshift({
      key: 'dashboard',
      name: 'My Dashboard',
      icon: <AddchartOutlined />,
      path: '/dashboard',
      tag: 'beta',
      children: dashboardMenuItems
    });
  }
  return (
    <List
      component='nav'
      sx={{
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'y',
        maxHeight: 'calc(100vh - 88px)',
        flex: 1
      }}
    >
      {navigationBarItems.map(({ name, icon, path, key, children, tag }) => {
        return (
          <NavigationItem
            key={key}
            item={{
              key,
              name,
              icon,
              path,
              children,
              tag
            }}
            parentPath={path}
          />
        );
      })}
    </List>
  );
};

export default NavBar;
